import { create } from "zustand";

/**
 * The server's structured paywall signal, lifted out of the axios error body.
 *
 * Before this existed, a paywall rejection rendered NOTHING. `translateError`
 * switches over the `ErrorMessage` enum, which contains no paywall codes, so
 * `description` came back null; the status is 403 so the 422 fallback was
 * skipped; and no toast was emitted. Every paywall hit in the product was a
 * silent no-op — not "an upgrade prompt without a CTA", but no feedback at all.
 */
export type PaywallSignal = {
  code: string;
  /** FeatureKey, present only on ENTITLEMENT_REQUIRED. */
  feature?: string;
  currentTier?: string;
};

type PaywallState = { signal: PaywallSignal | null };

export const usePaywallStore = create<PaywallState>()(() => ({ signal: null }));

/**
 * Idempotent by design: several hooks poll every 4–5 s (`use-review-queue`,
 * `useJobsFeed` while an application is in flight), and the query client sets
 * `retry: false` with no global `onError`, so nothing else de-duplicates. A
 * fire-on-every-error handler would re-open the dialog up to 12×/minute.
 */
export const openPaywall = (signal: PaywallSignal): void => {
  if (usePaywallStore.getState().signal) return;
  usePaywallStore.setState({ signal });
};

export const closePaywall = (): void => {
  usePaywallStore.setState({ signal: null });
};

/**
 * Explicit allowlist — never a `*_LIMIT_REACHED` suffix match.
 *
 * Deliberately EXCLUDED:
 * - `RESUME_ABSOLUTE_CAP_REACHED` — the safety ceiling for accounts that are
 *   already unlimited. Telling a paying customer to buy the plan they own is
 *   the failure mode; the server copy points at support.
 * - `RESUME_LIMIT_REACHED` — owned locally by the import dialog's toast action.
 * - `AI_TAILORING_DAILY_LIMIT_REACHED` — owned locally by `TailoringLimitDialog`,
 *   which offers three ways out. A global dialog would stack on top of it.
 * - `TIER_LIMIT_REACHED` — HTTP 429 from /tools/*, reachable by logged-OUT
 *   visitors who have no account to upgrade.
 */
const PAYWALL_CODES = new Set([
  "ENTITLEMENT_REQUIRED",
  "SUBSCRIPTION_REQUIRED",
  "APPLICATIONS_NOT_AVAILABLE_ON_TIER",
  "AI_TAILORING_NOT_AVAILABLE_ON_TIER",
  "APPLICATIONS_DAILY_LIMIT_REACHED",
  "APPLICATIONS_MONTHLY_LIMIT_REACHED",
  "JOB_PROFILES_LIMIT_REACHED",
]);

export const isPaywallCode = (code: unknown): code is string =>
  typeof code === "string" && PAYWALL_CODES.has(code);
